import { RRule, rrulestr } from 'rrule';

/**
 * Recurrence engine.
 *
 * Wraps rrule.js to compute the next occurrence of a recurring task. Two
 * modes are supported, mirroring the SmartAdd grammar:
 *
 *  - Fixed cadence ("every monday"):  fromCompletion = false. We seed the
 *    rule with `reference` as DTSTART, then ask rrule.js for the next
 *    occurrence strictly after `reference`.
 *
 *  - From completion ("after 3 days"): fromCompletion = true. RFC 5545 has
 *    no native concept for "schedule the next instance N units after I
 *    finished it", so we ignore the BYxxx parts and just step `INTERVAL`
 *    units of `FREQ` forward from `reference` (which is the completion date).
 */

export interface ParsedRecurrence {
  rrule: string;
  fromCompletion: boolean;
}

const FREQ_MS: Record<string, number | undefined> = {
  DAILY: 24 * 60 * 60 * 1000,
  WEEKLY: 7 * 24 * 60 * 60 * 1000,
};

interface FreqInterval {
  freq: string;
  interval: number;
}

function parseRruleParts(rruleStr: string): FreqInterval | null {
  const parts: Record<string, string> = {};
  for (const segment of rruleStr.split(';')) {
    const [k, v] = segment.split('=');
    if (k && v) parts[k] = v;
  }
  if (!parts.FREQ) return null;
  const interval = parts.INTERVAL ? Number.parseInt(parts.INTERVAL, 10) : 1;
  if (!Number.isFinite(interval) || interval <= 0) return null;
  return { freq: parts.FREQ, interval };
}

function addFromCompletion(reference: Date, info: FreqInterval): Date | null {
  const ms = FREQ_MS[info.freq];
  if (ms !== undefined) {
    return new Date(reference.getTime() + ms * info.interval);
  }
  // For MONTHLY / YEARLY we add calendar months / years using UTC accessors so
  // that the engine remains DST-agnostic.
  const result = new Date(reference.getTime());
  if (info.freq === 'MONTHLY') {
    result.setUTCMonth(result.getUTCMonth() + info.interval);
    return result;
  }
  if (info.freq === 'YEARLY') {
    result.setUTCFullYear(result.getUTCFullYear() + info.interval);
    return result;
  }
  return null;
}

function buildRule(rruleStr: string, dtstart: Date): RRule | null {
  try {
    // rrulestr accepts a full ICS-ish block. We feed DTSTART as a UTC value
    // so the rule expands in UTC and is timezone-stable.
    const dt = formatIcsUtc(dtstart);
    const rule = rrulestr(`DTSTART:${dt}\nRRULE:${rruleStr}`);
    return rule instanceof RRule ? rule : null;
  } catch {
    return null;
  }
}

function formatIcsUtc(d: Date): string {
  const pad = (n: number, w = 2): string => String(n).padStart(w, '0');
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}

/**
 * Compute the next single occurrence after `reference`.
 *  - For fixed cadence, `reference` is the previous due date.
 *  - For fromCompletion, `reference` is the completion date.
 *
 * Returns null if the rrule string can't be parsed.
 */
export function nextOccurrence(recurrence: ParsedRecurrence, reference: Date): Date | null {
  if (recurrence.fromCompletion) {
    const info = parseRruleParts(recurrence.rrule);
    if (!info) return null;
    return addFromCompletion(reference, info);
  }

  const rule = buildRule(recurrence.rrule, reference);
  if (!rule) return null;
  return rule.after(reference, false);
}

/**
 * List the next `count` occurrences after `reference`.
 *
 * For fromCompletion rules, each instance is one INTERVAL beyond the previous
 * (i.e. as if the user completed each one immediately on schedule).
 */
export function listNext(recurrence: ParsedRecurrence, reference: Date, count: number): Date[] {
  if (count <= 0) return [];

  if (recurrence.fromCompletion) {
    const info = parseRruleParts(recurrence.rrule);
    if (!info) return [];
    const out: Date[] = [];
    let cursor = reference;
    for (let i = 0; i < count; i++) {
      const next = addFromCompletion(cursor, info);
      if (!next) break;
      out.push(next);
      cursor = next;
    }
    return out;
  }

  const rule = buildRule(recurrence.rrule, reference);
  if (!rule) return [];
  const out: Date[] = [];
  let cursor: Date | null = reference;
  for (let i = 0; i < count; i++) {
    const next: Date | null = rule.after(cursor as Date, false);
    if (!next) break;
    out.push(next);
    cursor = next;
  }
  return out;
}
